import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Box
} from "@mui/material"
import InputMask from "react-input-mask"
import { sendInvite } from "../../Supabase/supabaseClient"

// Configurações do Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY

// Validação de CPF (dígitos verificadores)
function validarCPF(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, '')
  if (digits.length !== 11) return false
  if (/^(\d)\1{10}$/.test(digits)) return false // bloqueia sequências iguais ex: 111.111.111-11

  const calc = (len: number) => {
    let sum = 0
    for (let i = 0; i < len; i++) sum += parseInt(digits[i]) * (len + 1 - i)
    const rem = (sum * 10) % 11
    return rem === 10 || rem === 11 ? 0 : rem
  }
  return calc(9) === parseInt(digits[9]) && calc(10) === parseInt(digits[10])
}

// Função para atualizar cliente
async function atualizarCliente(
  id: number,
  dados: {
    nome: string
    email: string
    telefone: string
    cpf: string
    status?: string
    endereco: Endereco
    tipo_plano: string
    dispositivo?: string
  }
) {
  if (!supabaseUrl || !serviceKey) {
    throw new Error("Variáveis de ambiente do Supabase não definidas")
  }
  const response = await fetch(`${supabaseUrl}/rest/v1/cliente?id=eq.${id}`, {
    method: "PATCH",
    headers: {
      apikey: serviceKey,
      Authorization: `Bearer ${serviceKey}`,
      "Content-Type": "application/json",
      Prefer: "return=representation"
    },
    body: JSON.stringify(dados)
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(
      data.error_description || data.message || `Erro ${response.status}`
    )
  }
  return response.json()
}

interface Endereco {
  cep: string
  logradouro: string
  bairro: string
  cidade: string
  estado: string
  numero: string
  complemento: string
  ponto_referencia: string
}

export interface Cliente {
  id: number
  user_id: string
  nome: string
  email: string
  telefone: string
  status: string
  tipo_plano: "basico" | "premium" | "saude24h"
  endereco?: Endereco
  dispositivo?: string
}

// Função para buscar endereço por CEP
async function buscarEnderecoPorCEP(cep: string) {
  const cepLimpo = cep.replace(/\D/g, "")
  if (cepLimpo.length !== 8) return null

  try {
    const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`)
    const data = await response.json()

    if (data.erro) return null

    return {
      cep: data.cep,
      logradouro: data.logradouro,
      bairro: data.bairro,
      cidade: data.localidade,
      estado: data.uf
    }
  } catch (error) {
    console.error("Erro ao buscar CEP:", error)
    return null
  }
}

interface ClientDialogProps {
  open: boolean
  onClose: () => void
  initialData?: Cliente
  onSave: (data: Cliente) => void
  isEditing?: boolean
}

const ClientDialog: React.FC<ClientDialogProps> = ({
  open,
  onClose,
  initialData,
  onSave,
  isEditing
}) => {
  const [nome, setNome] = useState(initialData?.nome || "")
  const [email, setEmail] = useState(initialData?.email || "")
  const [password, setPassword] = useState("")
  const [telefone, setTelefone] = useState(initialData?.telefone || "")
  const [cpf, setCpf] = useState((initialData as any)?.cpf || "")
  const [loading, setLoading] = useState(false)
  const [tipoPlano, setTipoPlano] = useState(initialData?.tipo_plano || "")
  const [dispositivo, setDispositivo] = useState(initialData?.dispositivo || "")
  // Novos estados para endereço
  const [cep, setCep] = useState(initialData?.endereco?.cep || "")
  const [logradouro, setLogradouro] = useState(
    initialData?.endereco?.logradouro || ""
  )
  const [bairro, setBairro] = useState(initialData?.endereco?.bairro || "")
  const [cidade, setCidade] = useState(initialData?.endereco?.cidade || "")
  const [estado, setEstado] = useState(initialData?.endereco?.estado || "")
  const [numero, setNumero] = useState(initialData?.endereco?.numero || "")
  const [complemento, setComplemento] = useState(
    initialData?.endereco?.complemento || ""
  )
  const [pontoReferencia, setPontoReferencia] = useState(
    initialData?.endereco?.ponto_referencia || ""
  )
  const [loadingCep, setLoadingCep] = useState(false)

  // Estados de erro de validação
  const [emailError, setEmailError] = useState<string | null>(null)
  const [cpfError, setCpfError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setNome(initialData?.nome || "")
      setEmail(initialData?.email || "")
      setPassword("")
      setTelefone(initialData?.telefone || "")
      setCpf((initialData as any)?.cpf || "")
      setCpfError(null)
      setTipoPlano(initialData?.tipo_plano || "")
      setDispositivo(initialData?.dispositivo || "")
      setCep(initialData?.endereco?.cep || "")
      setLogradouro(initialData?.endereco?.logradouro || "")
      setBairro(initialData?.endereco?.bairro || "")
      setCidade(initialData?.endereco?.cidade || "")
      setEstado(initialData?.endereco?.estado || "")
      setNumero(initialData?.endereco?.numero || "")
      setComplemento(initialData?.endereco?.complemento || "")
      setPontoReferencia(initialData?.endereco?.ponto_referencia || "")
    }
  }, [initialData, open])

  const handleCepBlur = async () => {
    if (cep.length >= 8) {
      setLoadingCep(true)
      const data = await buscarEnderecoPorCEP(cep)
      if (data) {
        setLogradouro(data.logradouro)
        setBairro(data.bairro)
        setCidade(data.cidade)
        setEstado(data.estado)
      }
      setLoadingCep(false)
    }
  }

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const handleSave = async () => {
    if (!nome || !email || !telefone || !cpf || !tipoPlano || (!isEditing && !password)) {
      alert("Por favor, preencha todos os campos obrigatórios.")
      return
    }

    if (!validarCPF(cpf)) {
      setCpfError("CPF inválido")
      alert("CPF inválido")
      return
    }

    const enderecoData: Endereco = {
      cep,
      logradouro,
      bairro,
      cidade,
      estado,
      numero,
      complemento,
      ponto_referencia: pontoReferencia
    }

    setLoading(true)
    try {
      const trimmedEmail = email.trim()
      const registrationPassword = password

      if (isEditing && initialData?.id) {
        const clienteAtualizado = await atualizarCliente(initialData.id, {
          nome,
          email: trimmedEmail,
          telefone,
          cpf: cpf.replace(/\D/g, ''),
          status: "ativo",
          endereco: enderecoData,
          tipo_plano: tipoPlano,
          dispositivo: dispositivo
        })

        alert("Cliente atualizado com sucesso!")
        onSave(clienteAtualizado[0])
      } else {
        const cleanCpf = cpf.replace(/\D/g, '')

        // Criar no Auth E no Banco via Edge Function (Atômico)
        console.log("[ClientDialog] Criando/Vinculando usuário via Function")
        try {
          const tableData = {
            nome,
            telefone,
            cpf: cleanCpf,
            status: "ativo",
            endereco: enderecoData,
            tipo_plano: tipoPlano,
            dispositivo: dispositivo
          }
          
          const { user, record } = await sendInvite(trimmedEmail, registrationPassword, 'cliente', tableData)
          
          if (user && record) {
            alert("Cliente criado com sucesso!")
            onSave(record)
          }
        } catch (inviteErr: any) {
          if (inviteErr.status === 429 || inviteErr.message?.includes("rate limit")) {
             throw new Error("Limite de envio de e-mails atingido no Supabase. Aguarde alguns minutos.");
          }
          throw inviteErr
        }
      }

      onClose()
    } catch (error: any) {
      console.error("[ClientDialog] Erro:", error)
      
      const msg = error.message || ""
      const isDuplicate = msg.includes("duplicate") || msg.includes("23505")

      if (isDuplicate) {
        if (msg.toLowerCase().includes("cpf")) {
          alert("Este CPF já está em uso.")
        } else if (msg.toLowerCase().includes("email")) {
          alert("Este e-mail já está em uso.")
        } else {
          alert("Erro: Já existe um cadastro com estes dados (E-mail ou CPF).")
        }
      } else {
        alert(
          `Erro ao ${isEditing ? "atualizar" : "criar"} cliente: ${error.message}`
        )
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        {isEditing ? "Editar Cliente" : "Adicionar Cliente"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isEditing
            ? "Edite as informações do cliente abaixo."
            : "Preencha as informações abaixo para adicionar um novo cliente."}
        </DialogContentText>
        <TextField
          autoFocus
          margin='dense'
          label='Nome Completo'
          type='text'
          fullWidth
          variant='outlined'
          value={nome}
          onChange={e => setNome(e.target.value)}
          required
        />
        <TextField
          fullWidth
          label='Email'
          type='email'
          margin='dense'
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            if (!validateEmail(e.target.value)) {
              setEmailError("Digite um e-mail válido")
            } else {
              setEmailError(null)
            }
          }}
          required
          error={!!emailError}
          helperText={emailError}
        />
        {!isEditing && (
          <TextField
            margin="normal"
            label="Senha"
            type="password"
            fullWidth
            required={!isEditing}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText={isEditing ? "Preencha apenas se quiser alterar a senha." : ""}
          />
        )}
        <TextField
          margin='dense'
          label='Telefone'
          type='text'
          fullWidth
          value={telefone}
          onChange={e => setTelefone(e.target.value)}
          InputProps={{
            inputComponent: InputMask as any,
            inputProps: {
              mask: "(99) 99999-9999",
              maskChar: null
            }
          }}
          required
        />
        <TextField
          margin='dense'
          label='CPF'
          type='text'
          fullWidth
          value={cpf}
          onChange={e => {
            setCpf(e.target.value)
            setCpfError(null)
          }}
          InputProps={{
            inputComponent: InputMask as any,
            inputProps: {
              mask: "999.999.999-99",
              maskChar: null
            }
          }}
          error={!!cpfError}
          helperText={cpfError}
          required
        />

        <FormControl fullWidth margin='dense' required>
          <InputLabel>Plano</InputLabel>
          <Select
            value={tipoPlano}
            label='Plano'
            onChange={e => setTipoPlano(e.target.value)}
          >
            <MenuItem value='basico'>Básico</MenuItem>
            <MenuItem value='premium'>Premium</MenuItem>
            <MenuItem value='saude24h'>Saúde 24h</MenuItem>
          </Select>
        </FormControl>

        <TextField
          margin='dense'
          label='Dispositivo'
          type='text'
          fullWidth
          value={dispositivo}
          onChange={e => setDispositivo(e.target.value)}
          helperText='ID ou Nome do dispositivo (opcional)'
        />

        {/* Seção de Endereço */}
        <Box sx={{ mt: 3, mb: 1 }}>
          <DialogContentText
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            Endereço
          </DialogContentText>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
          <TextField
            label='CEP'
            value={cep}
            onChange={e => setCep(e.target.value)}
            onBlur={handleCepBlur}
            sx={{ width: "150px" }}
            InputProps={{
              inputComponent: InputMask as any,
              inputProps: {
                mask: "99999-999",
                maskChar: null
              },
              endAdornment: loadingCep ? (
                <CircularProgress size={20} />
              ) : null
            }}
          />
          <TextField
            label='Logradouro'
            fullWidth
            value={logradouro}
            onChange={e => setLogradouro(e.target.value)}
          />
          <TextField
            label='Número'
            value={numero}
            onChange={e => setNumero(e.target.value)}
            sx={{ width: "250px" }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
          <TextField
            label='Bairro'
            fullWidth
            value={bairro}
            onChange={e => setBairro(e.target.value)}
          />
          <TextField
            label='Cidade'
            fullWidth
            value={cidade}
            onChange={e => setCidade(e.target.value)}
          />
          <TextField
            label='Estado'
            value={estado}
            onChange={e => setEstado(e.target.value)}
            sx={{ width: "250px" }}
            inputProps={{ maxLength: 2 }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label='Complemento'
            fullWidth
            value={complemento}
            onChange={e => setComplemento(e.target.value)}
          />
          <TextField
            label='Ponto de Referência'
            fullWidth
            value={pontoReferencia}
            onChange={e => setPontoReferencia(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={loading || !!emailError || !!cpfError}
        >
          {loading ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ClientDialog
